import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ReactComponent as AddPhotoIcon } from '../../../Assets/add_photo_alternate.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { faPencilAlt, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import APIGet from '../../../API/APIGet';
import APIPost from '../../../API/APIPost';
import APIPut from '../../../API/APIPut';
import APIDelete from '../../../API/APIDelete';
import { fileToBase64 } from '../../../Helper/FileHelper';
import { toast } from 'react-toastify';

const VISA_COUNTRY_ID = 1;

function extractVisaList(raw) {
	if (raw == null) return [];
	if (Array.isArray(raw)) return raw;
	if (Array.isArray(raw.data)) return raw.data;
	if (raw.data && Array.isArray(raw.data.visas)) return raw.data.visas;
	if (Array.isArray(raw.visas)) return raw.visas;
	return [];
}

function imageUrlFromApiItem(item) {
	const img = item.visaImage != null && item.visaImage !== '' ? item.visaImage : null;
	const fromBinary = item.imageBinary != null && item.imageBinary !== '' ? item.imageBinary : null;
	const raw = img || fromBinary || item.imageUrl || item.image;
	if (raw == null || raw === '') return '';
	const s = String(raw);
	if (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:')) return s;
	return 'data:image/jpeg;base64,' + s;
}

function normalizeVisaItem(item) {
	const id = item.id != null ? item.id : item.visaId;
	const remark = item.remark != null ? String(item.remark) : '';
	const image = imageUrlFromApiItem(item);
	return { id, remark, image };
}

export const VisaTab = ({ traveler, travelerId, MAX_IMAGE_BYTES }) => {
	const [visas, setVisas] = useState([]);
	const [isLoadingVisas, setIsLoadingVisas] = useState(false);
	const [deleteConfirmVisaId, setDeleteConfirmVisaId] = useState(null);
	const [isDeletingVisa, setIsDeletingVisa] = useState(false);

	const visaFileInputRef = useRef(null);

	const loadVisas = useCallback(async () => {
		if (!travelerId) return;
		setIsLoadingVisas(true);
		try {
			const result = await APIGet('travelers/' + travelerId + '/visas');
			const payload = result && result.data != null ? result.data : null;
			const list = extractVisaList(payload);
			setVisas(list.map(normalizeVisaItem));
		} catch (e) {
			console.log(e);
			setVisas([]);
		} finally {
			setIsLoadingVisas(false);
		}
	}, [travelerId]);

	useEffect(() => {
		loadVisas();
	}, [loadVisas]);

	const handleVisaAddClick = () => {
		if (visaFileInputRef.current) {
			visaFileInputRef.current.click();
		}
	};

	const handleRemarkSave = async (visaId, remark) => {
		if (!travelerId || visaId == null) return;
		const request = await APIPut('travelers/' + travelerId + '/visas/' + visaId, { id: visaId, remark, countryId: VISA_COUNTRY_ID });
		if (request && request.status >= 200 && request.status < 300) {
			await loadVisas();
			toast.success('บันทึกรายละเอียด VISA สำเร็จ');
		} else {
			console.log('Failed to update VISA remark', request);
			toast.error('ไม่สามารถบันทึกรายละเอียด VISA ได้ กรุณาลองใหม่อีกครั้ง');
		}
	};

	const handleReplaceVisaImage = async (visaId, remark, file) => {
		if (!travelerId || visaId == null || !file) return;
		if (file.size > MAX_IMAGE_BYTES) {
			toast.error('ขนาดรูปภาพมีขนาดใหญ่เกินไป');
			if (visaFileInputRef.current) visaFileInputRef.current.value = '';
			return;
		}
		const imageBinary = await fileToBase64(file);
		const request = await APIPut('travelers/' + travelerId + '/visas/' + visaId, { id: visaId, remark, imageBinary, countryId: VISA_COUNTRY_ID });
		if (request && request.status >= 200 && request.status < 300) {
			await loadVisas();
			toast.success('บันทึกรูปภาพ VISA สำเร็จ');
		} else {
			console.log('Failed to update VISA image', request);
			toast.error('ไม่สามารถบันทึกรูปภาพ VISA ได้ กรุณาลองใหม่อีกครั้ง');
		}
	};

	const confirmDeleteVisa = async visaId => {
		if (!travelerId || visaId == null) return;
		setIsDeletingVisa(true);
		try {
			const result = await APIDelete('travelers/' + travelerId + '/visas/' + visaId, {});
			if (result && result.status >= 200 && result.status < 300) {
				setDeleteConfirmVisaId(null);
				await loadVisas();
				toast.success('ลบ VISA สำเร็จ');
			} else {
				console.log('Failed to delete VISA', result);
			}
		} catch (e) {
			console.log(e);
		} finally {
			setIsDeletingVisa(false);
		}
	};

	const handleUploadVisa = async event => {
		const file = event.target.files && event.target.files[0];
		if (!file || !travelerId) return;
		console.log('file size', file.size);
		if (file.size > MAX_IMAGE_BYTES) {
			toast.error('ขนาดรูปภาพมีขนาดใหญ่เกินไป');
			if (visaFileInputRef.current) visaFileInputRef.current.value = '';
			return;
		}
		try {
			const imageBinary = await fileToBase64(file);
			const request = await APIPost('travelers/' + travelerId + '/visas', {
				imageBinary,
				remark: '',
				countryId: VISA_COUNTRY_ID,
			});
			if (request && request.status >= 200 && request.status < 300) {
				await loadVisas();
				toast.success('บันทึกรูปภาพ VISA สำเร็จ');
			} else {
				console.log('Failed to add VISA', request);
				if (request.status === 413) {
					toast.error('ขนาดรูปภาพมีขนาดใหญ่เกินไป');
				} else {
					toast.error('ไม่สามารถเพิ่ม VISA ได้');
				}
			}
		} catch (e) {
			console.log(e);
			toast.error('ไม่สามารถเพิ่ม VISA ได้');
		} finally {
			if (visaFileInputRef.current) visaFileInputRef.current.value = '';
		}
	};

	const visasWithImages = visas.filter(v => v.image);
	const showOnlyAddPlaceholder = !isLoadingVisas && visasWithImages.length === 0;

	return (
		<div className="traveler-docs">
			<div className="columns">
				<div className="column is-12" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
					{isLoadingVisas ? <span style={{ alignSelf: 'center', color: '#888' }}>กำลังโหลด...</span> : null}
					{!isLoadingVisas && visasWithImages.length > 0
						? visasWithImages.map(visa => (
								<VisaCard
									key={visa.id != null ? String(visa.id) : visa.image}
									visa={visa}
									onRemarkSave={handleRemarkSave}
									onReplaceImage={handleReplaceVisaImage}
									isDeleteConfirm={deleteConfirmVisaId != null && String(deleteConfirmVisaId) === String(visa.id)}
									onDeleteClick={() => setDeleteConfirmVisaId(visa.id)}
									onDeleteConfirm={() => confirmDeleteVisa(visa.id)}
									onDeleteCancel={() => setDeleteConfirmVisaId(null)}
									isDeletingVisa={isDeletingVisa}
								/>
						  ))
						: null}
					<div
						className=""
						onClick={handleVisaAddClick}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handleVisaAddClick();
							}
						}}
						role="button"
						tabIndex={0}
						style={{ width: 100, height: 100, marginLeft: 20, marginTop: 10, cursor: 'pointer' }}
						aria-label={showOnlyAddPlaceholder ? 'เพิ่มรูป' : undefined}
					>
						<AddPhotoIcon className="traveler-doc-image" style={{ color: 'green' }} />
						<input ref={visaFileInputRef} className="traveler-file-input" type="file" accept="image/*" onChange={handleUploadVisa} />
					</div>
				</div>
			</div>
		</div>
	);
};

const VisaCard = ({ visa, onRemarkSave, onReplaceImage, isDeleteConfirm, onDeleteClick, onDeleteConfirm, onDeleteCancel, isDeletingVisa }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draftRemark, setDraftRemark] = useState(visa.remark);
	const [fullImageSrc, setFullImageSrc] = useState(null);
	const replaceFileInputRef = useRef(null);

	useEffect(() => {
		setDraftRemark(visa.remark);
	}, [visa.remark]);

	const closeLightbox = useCallback(() => setFullImageSrc(null), []);

	useEffect(() => {
		if (!fullImageSrc) return undefined;
		const onKeyDown = e => {
			if (e.key === 'Escape') closeLightbox();
		};
		document.addEventListener('keydown', onKeyDown);
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.body.style.overflow = '';
		};
	}, [fullImageSrc, closeLightbox]);

	const saveRemark = async () => {
		await onRemarkSave(visa.id, draftRemark);
		setIsEditing(false);
	};

	return (
		<div className="traveler-doc-card">
			{isDeleteConfirm ? (
				<div
					className="traveler-doc-remove-confirm"
					style={{ display: 'flex', justifyContent: 'center', gap: 8 }}
					onClick={event => event.stopPropagation()}
					role="presentation"
				>
					<button
						type="button"
						className="button is-small"
						style={{
							backgroundColor: '#DC1818',
							borderColor: '#DC1818',
							color: '#fff',
							opacity: isDeletingVisa ? 0.7 : 1,
						}}
						onClick={event => {
							event.stopPropagation();
							onDeleteConfirm();
						}}
						disabled={isDeletingVisa}
						aria-label="ลบ"
						title="ลบ"
					>
						<FontAwesomeIcon icon={faTrashAlt} />
					</button>
					<button
						type="button"
						className="button is-small"
						style={{
							backgroundColor: '#22c65b',
							borderColor: '#22c65b',
							color: '#fff',
							opacity: isDeletingVisa ? 0.7 : 1,
						}}
						onClick={event => {
							event.stopPropagation();
							onDeleteCancel();
						}}
						disabled={isDeletingVisa}
						aria-label="ยกเลิก"
						title="ยกเลิก"
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>
			) : (
				<button
					type="button"
					className="traveler-doc-remove"
					onClick={event => {
						event.stopPropagation();
						onDeleteClick();
					}}
					aria-label="ลบรูป"
					title="ลบรูป"
				>
					<FontAwesomeIcon icon={faTrashAlt} />
				</button>
			)}
			<button type="button" className="traveler-doc-image-btn" onClick={() => setFullImageSrc(visa.image)} aria-label="View VISA image full size">
				<img className="traveler-doc-image" src={visa.image} alt="VISA" />
			</button>
			<button
				type="button"
				className="button is-small is-text"
				style={{ padding: '2px 0', fontSize: 12 }}
				onClick={() => replaceFileInputRef.current && replaceFileInputRef.current.click()}
			>
				เปลี่ยนรูป
			</button>
			<input
				ref={replaceFileInputRef}
				className="traveler-file-input"
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				onChange={async e => {
					const file = e.target.files && e.target.files[0];
					if (!file) return;
					try {
						await onReplaceImage(visa.id, isEditing ? draftRemark : visa.remark, file);
					} finally {
						if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
					}
				}}
			/>
			<div className="columns" style={{ marginTop: 5 }}>
				<div className="column is-10">
					{isEditing ? (
						<textarea
							className="input"
							type="text"
							value={draftRemark}
							placeholder="รายละเอียดอื่นๆ"
							style={{ minHeight: 100 }}
							onChange={event => setDraftRemark(event.target.value)}
						/>
					) : (
						<span style={{ color: visa.remark ? 'black' : '#999' }}>{visa.remark || 'รายละเอียดอื่นๆ'}</span>
					)}
				</div>
				<div className="column is-2">
					{isEditing ? (
						<button className="button is-small" style={{ backgroundColor: 'transparent', border: 'none' }} onClick={saveRemark}>
							<FontAwesomeIcon icon={faCheck} style={{ color: 'green', fontSize: 16 }} />
						</button>
					) : (
						<button className="button is-small" style={{ backgroundColor: 'transparent', border: 'none' }} onClick={() => setIsEditing(true)}>
							<FontAwesomeIcon icon={faPencilAlt} />
						</button>
					)}
				</div>
			</div>
			{fullImageSrc ? (
				<div className="traveler-doc-lightbox" onClick={closeLightbox} onKeyDown={e => e.key === 'Escape' && closeLightbox()} role="presentation">
					<button type="button" className="traveler-doc-lightbox-close" onClick={closeLightbox} aria-label="Close">
						×
					</button>
					<img className="traveler-doc-lightbox-img" src={fullImageSrc} alt="VISA full size" onClick={e => e.stopPropagation()} />
				</div>
			) : null}
		</div>
	);
};
